import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

const ResetPassword = () => {
  const navigate = useNavigate();

  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Clicking the emailed reset link redirects here with a recovery token
    // in the URL hash. The Supabase client (detectSessionInUrl: true) parses
    // it automatically and fires a PASSWORD_RECOVERY auth event.
    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValid(true);
        setIsValidating(false);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setIsValid(true);
      setIsValidating(false);
    });

    return () => subscription.subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      toast.error(error.message || 'Failed to reset password');
    } else {
      setIsSuccess(true);
      toast.success('Password reset successfully!');
    }

    setIsSubmitting(false);
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] via-[#E0F7FA] to-[#FFF4E6] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
          <div className="animate-spin w-12 h-12 border-4 border-[#81B29A] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[#4A5568]">Verifying your reset link...</p>
        </div>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] via-[#E0F7FA] to-[#FFF4E6] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-[#2D3748] mb-4">Invalid reset link</h2>
          <p className="text-[#4A5568] mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] via-[#E0F7FA] to-[#FFF4E6] flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-[#2D3748] mb-4">Password reset!</h2>
          <p className="text-[#4A5568] mb-6">
            Your password has been successfully reset. You can now sign in with your new password.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-8 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFBF7] via-[#E0F7FA] to-[#FFF4E6] flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#81B29A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#81B29A]" />
          </div>
          <h2 className="text-2xl font-bold text-[#2D3748]">Reset your password</h2>
          <p className="text-[#4A5568] mt-2">Enter a new password for your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#81B29A]/20 focus:border-[#81B29A] outline-none transition-all pr-12"
              required
              minLength={6}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#2D3748]"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-slate-50 focus:ring-2 focus:ring-[#81B29A]/20 focus:border-[#81B29A] outline-none transition-all"
            required
            minLength={6}
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#F4A62A] text-[#2D3748] hover:bg-[#DB9111] px-6 py-4 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {isSubmitting ? 'Resetting...' : 'Reset password'}
          </button>
        </form>

        <p className="text-center mt-6 text-[#4A5568]">
          Remember your password?{' '}
          <button
            onClick={() => navigate('/')}
            className="text-[#81B29A] font-semibold hover:underline"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
